/**
 * What Enter does in a length field (#1151).
 *
 * With a mouse and a hardware keyboard, Enter commits and leaves the field, so
 * the canvas's shortcuts work again straight away — that stays.
 *
 * With a tablet's on-screen keyboard, leaving the field closes the keyboard,
 * and "place roughly, then type the exact width AND height" meant dismissing
 * it after the width only to bring it back for the height. So a field focused
 * by touch (or a stylus) moves on to the next length field in the same panel,
 * the way a form does, and the keyboard's Enter key says so ("next"). The last
 * field, or one with nothing after it, still leaves ("done").
 *
 * Moving focus blurs the field, so its `change` commit fires exactly as it did
 * when Enter blurred it.
 */

/** The next visible length field after `input` in its panel, in the same unit
 *  column when that row shows one. */
function nextLengthField(input: HTMLInputElement): HTMLInputElement | null {
  const row = input.closest(".mm-in-row");
  if (!row) return null;
  const root =
    input.closest("#properties-panel, .properties-split, .modal") ??
    input.ownerDocument;
  const rows = [...root.querySelectorAll<HTMLElement>(".mm-in-row")].filter(
    (r) => r.getClientRects().length > 0,
  );
  const at = rows.indexOf(row as HTMLElement);
  const next = at >= 0 ? rows[at + 1] : undefined;
  if (!next) return null;
  const inputsOf = (r: Element) =>
    [...r.querySelectorAll<HTMLInputElement>("input")].filter(
      (i) => !i.disabled && i.getClientRects().length > 0,
    );
  const column = inputsOf(row).indexOf(input);
  const candidates = inputsOf(next);
  return candidates[column] ?? candidates[0] ?? null;
}

/** Fields focused by a finger or stylus, until they lose focus. */
const touchFocused = new WeakSet<HTMLInputElement>();

/** Wire Enter on a length field: next field for a touch-focused field, leave
 *  the field otherwise. Replaces a plain blur-on-Enter. */
export function wireLengthFieldEnter(input: HTMLInputElement): void {
  input.addEventListener("pointerdown", (e) => {
    if (e.pointerType === "mouse") touchFocused.delete(input);
    else touchFocused.add(input);
  });
  input.addEventListener("focus", () => {
    input.enterKeyHint =
      touchFocused.has(input) && nextLengthField(input) ? "next" : "done";
  });
  input.addEventListener("blur", () => {
    touchFocused.delete(input);
  });
  input.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" || e.isComposing) return;
    const next = touchFocused.has(input) ? nextLengthField(input) : null;
    if (!next) {
      input.blur();
      return;
    }
    e.preventDefault();
    // Reached from a touch-focused field, so it answers Enter the same way.
    touchFocused.add(next);
    next.focus();
    next.select();
  });
}
